
import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import QuizPage from './pages/QuizPage';
import FlashcardsPage from './pages/FlashcardsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import LoginPage from './pages/LoginPage';
import NewRegisterPage from './pages/NewRegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminUsersPage from './pages/AdminUsersPage';
import ParentDashboard from './pages/ParentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import ClassDetailsPage from './pages/ClassDetailsPage';
import TestCenterPage from './pages/TestCenterPage';
import ActiveTestPage from './pages/ActiveTestPage';
import { authService } from './services/authService';
import AdminAIReportPage from './pages/AdminAIReportPage';
import { User, UserRole } from './types';

// Navbar Component to handle logic inside Router context
const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Re-check auth on route change
    setUser(authService.getCurrentUser());
  }, [location]);

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    navigate('/');
  };


  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img src="/logo.png" alt="CleverCubby" className="h-10 w-10 mr-2 rounded-lg object-contain" />
              <span className="font-bold text-2xl tracking-tight text-brand-blue brand-font">CleverCubby</span>
            </Link>

            {user && (
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                <Link to="/dashboard" className={`border-transparent hover:border-brand-blue hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/dashboard' ? 'border-brand-blue text-gray-900' : 'text-gray-500'}`}>
                  Dashboard
                </Link>
                <Link to="/leaderboard" className={`border-transparent hover:border-brand-blue hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/leaderboard' ? 'border-brand-blue text-gray-900' : 'text-gray-500'}`}>
                  Leaderboard
                </Link>
                {(user.role === UserRole.STUDENT || user.role === UserRole.TEACHER) && (
                  <Link to="/tests" className={`border-transparent hover:border-indigo-600 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.includes('/tests') ? 'border-indigo-600 text-gray-900' : 'text-gray-500'}`}>
                    Test Center
                  </Link>
                )}
                {user.role === UserRole.PARENT && (
                  <Link to="/parent" className={`border-transparent hover:border-purple-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/parent' ? 'border-purple-500 text-gray-900' : 'text-gray-500'}`}>
                    My Children
                  </Link>
                )}
                {user.role === UserRole.TEACHER && (
                  <Link to="/teacher" className={`border-transparent hover:border-teal-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/teacher' ? 'border-teal-500 text-gray-900' : 'text-gray-500'}`}>
                    Classroom
                  </Link>
                )}
                {user.role === UserRole.ADMIN && (
                  <Link to="/admin" className={`border-transparent hover:border-brand-blue hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/admin' ? 'border-brand-blue text-gray-900' : 'text-gray-500'}`}>
                    Admin Panel
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <div className="flex items-center">
                  <div className="hidden md:block text-right mr-3">
                    <p className="text-sm font-bold text-slate-700">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.role}</p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-brand-accent flex items-center justify-center text-indigo-900 font-bold">
                    {user.name.charAt(0)}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-red-500 text-sm font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              !location.pathname.includes('login') && !location.pathname.includes('register') && (
                <Link to="/login" className="text-brand-blue font-bold hover:text-indigo-700">
                  Log In
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <div className="min-h-screen flex flex-col font-sans text-slate-800 bg-slate-50">
        <NavBar />

        {/* Main Content */}
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage />} />

            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<NewRegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Protected Routes - In a real app, wrap in <RequireAuth> */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/flashcards" element={<FlashcardsPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />

            {/* Parent Routes */}
            <Route path="/parent" element={<ParentDashboard />} />
            <Route path="/parent/child/:childId" element={<Dashboard />} />

            {/* Teacher Routes */}
            <Route path="/teacher" element={<TeacherDashboard />} />
            <Route path="/teacher/classes/:classId" element={<ClassDetailsPage />} />

            {/* Test Center Routes */}
            <Route path="/tests" element={<TestCenterPage />} />
            <Route path="/test/active" element={<ActiveTestPage />} />


            {/* Admin Route */}
            <Route path="/admin" element={<AdminUsersPage />} />
            <Route path="/admin/ai-report" element={<AdminAIReportPage />} />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 mt-auto">
          <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <img src="/logo.png" alt="Logo" className="h-8 w-8 mr-2 opacity-50 grayscale object-contain" />
                <span className="font-bold text-lg text-slate-400 brand-font">CleverCubby</span>
              </div>
              <p className="text-center text-base text-gray-400">
                &copy; 2024 CleverCubby Learning. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </HashRouter>
  );
};

export default App;
